import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AsyncJobType } from '@recruitee/account/data';
import { AsyncJobService } from '@recruitee/async-job/data';
import { HttpApiService } from '@recruitee/http';
import {
  CandidateReferrersReference,
  FullCandidate,
  OfferBriefReference,
  StageReference,
  UserReference,
} from '@recruitee/http/types';

import { formatCandidateParams } from './helpers';

export interface CopyToCompanyParams {
  id: number;
  offerId: number;
  companyId: number;
}

export interface DeleteParsedCvParams {
  id: number;
  deleteParsedData: boolean;
}

export interface UpdateCvParams {
  id: number;
  file: File;
}

export interface UpdateParseCvParams {
  id: number;
  file: File;
  parse: any;
}

export interface SaveParams {
  data: Partial<FullCandidate>;
  offerIds?: number[] | null;
  formData?: boolean;
}

export type FullCandidateReferences =
  | OfferBriefReference
  | StageReference
  | UserReference
  | CandidateReferrersReference;

export interface FullCandidateResponse {
  candidate: FullCandidate;
  references: FullCandidateReferences[];
}

@Injectable({ providedIn: 'root' })
export class FullCandidateHttpClient {
  constructor(protected httpApi: HttpApiService, protected asyncJob: AsyncJobService) {}

  public create({
    data,
    offerIds = null,
    formData = false,
  }: SaveParams): Observable<FullCandidateResponse> {
    const body = formatCandidateParams({ data, offerIds }, formData);
    return this.httpApi.post(`/candidates`, {}, body);
  }

  public update({
    data,
    offerIds = null,
    formData = false,
  }: SaveParams): Observable<FullCandidateResponse> {
    const body = formatCandidateParams({ data, offerIds }, formData);
    return this.httpApi.patch(`/candidates/${data.id}`, {}, body);
  }

  public updateCv({ id, file }: UpdateCvParams): Observable<FullCandidateResponse> {
    const formData = new FormData();
    formData.append('candidate[cv]', file);

    return this.httpApi.patch(`/candidates/${id}/update_cv`, {}, formData);
  }

  public updateParseCv({
    id,
    file,
    parse,
  }: UpdateParseCvParams): Observable<FullCandidateResponse> {
    const formData = new FormData();
    formData.append('candidate[cv]', file);
    formData.append('parse', parse);

    return this.httpApi.patch(`/candidates/${id}/update_cv`, {}, formData);
  }

  public deleteParsedCv({
    id,
    deleteParsedData,
  }: DeleteParsedCvParams): Observable<FullCandidateResponse> {
    return this.httpApi.delete(
      `/candidates/${id}/delete_cv?delete_parsed_data=${deleteParsedData}`,
    );
  }

  public fetchOne(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.get(`/candidates/${id}`);
  }

  public removePhoto(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/remove_photo`);
  }

  public deleteCv(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.delete(`/candidates/${id}/delete_cv`);
  }

  public reparseCv(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.post(`/candidates/${id}/parse_cv`);
  }

  public view(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/view`);
  }

  public follow(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/follow`);
  }

  public unfollow(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/unfollow`);
  }

  public retrieve(id: number): Observable<FullCandidateResponse> {
    return this.httpApi.post(`/candidates/${id}/retrieve`);
  }

  public merge(id: number): Observable<{ uuid: string }> {
    return this.httpApi
      .patch(`/candidates/${id}/merge`)
      .pipe(switchMap(this.asyncJob.trackAsyncJobProgress(AsyncJobType.CandidateMerge)));
  }

  public copyToCompany({
    id,
    offerId,
    companyId,
  }: CopyToCompanyParams): Observable<FullCandidateResponse> {
    return this.httpApi.patch(`/candidates/${id}/copy_to_company`, {
      offer_id: offerId,
      to_company_id: companyId,
    });
  }
}
